import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { authAPI } from '../services/api';
import { STORAGE_KEYS } from '../utils/constants';
import { setStorageItem, getStorageItem, removeStorageItem } from '../utils/helpers';
import tokenService from '../services/tokenService';
import { setSentryUser } from '../config/sentry';

// Initial state
const initialState = {
  user: null,
  token: null,
  isAuthenticated: false,
  isLoading: true,
  error: null,
};

// Action types
const AUTH_ACTIONS = {
  LOGIN_START: 'LOGIN_START',
  LOGIN_SUCCESS: 'LOGIN_SUCCESS',
  LOGIN_ERROR: 'LOGIN_ERROR',
  LOGOUT: 'LOGOUT',
  SET_USER: 'SET_USER',
  SET_LOADING: 'SET_LOADING',
  CLEAR_ERROR: 'CLEAR_ERROR',
};

// Reducer
const authReducer = (state, action) => {
  switch (action.type) {
    case AUTH_ACTIONS.LOGIN_START:
      return {
        ...state,
        // No cambiar isLoading aquí - el componente Login usa isSubmitting local
        error: null,
      };

    case AUTH_ACTIONS.LOGIN_SUCCESS:
      return {
        ...state,
        user: action.payload.user,
        token: action.payload.token,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      };

    case AUTH_ACTIONS.LOGIN_ERROR:
      return {
        ...state,
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
        error: action.payload,
      };

    case AUTH_ACTIONS.LOGOUT:
      return {
        ...state,
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
      };

    case AUTH_ACTIONS.SET_USER:
      return {
        ...state,
        user: action.payload,
        isAuthenticated: !!action.payload,
        isLoading: false,
      };

    case AUTH_ACTIONS.SET_LOADING:
      return {
        ...state,
        isLoading: action.payload,
      };

    case AUTH_ACTIONS.CLEAR_ERROR:
      return {
        ...state,
        error: null,
      };

    default:
      return state;
  }
};

// Create context
const AuthContext = createContext();

// Provider component
export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Initialize auth state - compatible with HttpOnly cookies
  useEffect(() => {
    const initializeAuth = async () => {
      // No intentar verificar sesión si estamos en rutas públicas
      // Nota: "/" NO está aquí porque necesita verificar cookies para redirigir usuarios autenticados
      const publicRoutes = ['/login', '/privacy', '/payment-success', '/payment-cancel'];
      if (publicRoutes.includes(window.location.pathname)) {
        dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: false });
        return;
      }

      // Con HttpOnly cookies, no podemos verificar si hay un token en localStorage
      // En su lugar, intentamos verificar la sesión con el backend
      try {
        // Intentar obtener datos del usuario (las cookies se envían automáticamente)
        const userData = await authAPI.me(true);

        // Intentar obtener un token para WebSocket
        let wsToken = null;
        try {
          const wsTokenResponse = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:8000'}/api/v1/auth/ws-token`, {
            credentials: 'include', // Enviar cookies
            headers: {
              'Content-Type': 'application/json'
            }
          });
          if (wsTokenResponse.ok) {
            const data = await wsTokenResponse.json();
            wsToken = data.token;
          }
        } catch (e) {
          console.warn('Could not get WebSocket token, WebSocket may not work:', e);
        }

        dispatch({
          type: AUTH_ACTIONS.LOGIN_SUCCESS,
          payload: { user: userData, token: wsToken }, // Token para WebSocket
        });

        // Configurar contexto de usuario en Sentry
        setSentryUser(userData);

        // Guardar usuario en localStorage para persistencia de datos de usuario
        setStorageItem(STORAGE_KEYS.USER, userData);

        // Con HttpOnly cookies, el refresh automático se maneja en el interceptor
        // No necesitamos interval manual
      } catch (error) {
        // Solo limpiar si es un error definitivo
        const is401 = error.response?.status === 401;
        const isRefreshError = error.message?.includes('refresh') || error.config?.url?.includes('/auth/refresh');

        // Si es un 401 pero no es un error de refresh, el interceptor puede estar refrescando
        if (is401 && !isRefreshError) {
          // Dar tiempo al interceptor para intentar el refresh
          dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: false });
          return;
        }

        // Para otros errores o errores definitivos de refresh, limpiar todo
        tokenService.clearTokens();
        removeStorageItem(STORAGE_KEYS.USER);
        dispatch({ type: AUTH_ACTIONS.LOGOUT });
      }
    };

    initializeAuth();

    // Cleanup interval on unmount
    return () => {
      if (window.tokenRefreshInterval) {
        clearInterval(window.tokenRefreshInterval);
        window.tokenRefreshInterval = null;
      }
    };
  }, []);

  // Login function - compatible with HttpOnly cookies
  const login = async (credentials) => {
    dispatch({ type: AUTH_ACTIONS.LOGIN_START });

    try {
      // Login (los tokens se setean automáticamente como HttpOnly cookies)
      // También obtenemos el access_token en el response para usarlo en WebSocket
      const loginResponse = await authAPI.login(credentials);

      // Obtener la información del usuario
      const user = await authAPI.me();

      // Guardar usuario en localStorage para datos de usuario
      setStorageItem(STORAGE_KEYS.USER, user);

      dispatch({
        type: AUTH_ACTIONS.LOGIN_SUCCESS,
        payload: { user, token: loginResponse.access_token }, // Guardar access_token para WebSocket
      });

      // Configurar contexto de usuario en Sentry
      setSentryUser(user);

      return { success: true };
    } catch (error) {
      // Limpiar cualquier dato que pueda haberse guardado
      tokenService.clearTokens();
      removeStorageItem(STORAGE_KEYS.USER);

      // Mensaje específico para credenciales incorrectas (401)
      let errorMessage;
      if (error.response?.status === 401) {
        errorMessage = 'Credenciales incorrectas';
      } else {
        errorMessage = error.response?.data?.detail || error.response?.data?.message || 'Error de autenticación';
      }

      dispatch({
        type: AUTH_ACTIONS.LOGIN_ERROR,
        payload: errorMessage,
      });

      return { success: false, error: errorMessage };
    }
  };

  // Logout function
  const logout = async (logoutAllDevices = false) => {
    try {
      // Revoke refresh tokens before logout
      if (logoutAllDevices) {
        await tokenService.revokeAllRefreshTokens();
      } else {
        await tokenService.revokeRefreshToken();
      }

      // Call logout API if exists
      await authAPI.logout();
    } catch (error) {
      console.error('Error during logout:', error);
    } finally {
      // Clear auto-refresh interval
      if (window.tokenRefreshInterval) {
        clearInterval(window.tokenRefreshInterval);
        window.tokenRefreshInterval = null;
      }

      // Clear all tokens and user data
      tokenService.clearTokens();
      removeStorageItem(STORAGE_KEYS.USER);

      // Limpiar contexto de usuario en Sentry
      setSentryUser(null);

      dispatch({ type: AUTH_ACTIONS.LOGOUT });
    }
  };

  // Update user data
  const updateUser = (userData) => {
    setStorageItem(STORAGE_KEYS.USER, userData);
    dispatch({
      type: AUTH_ACTIONS.SET_USER,
      payload: userData,
    });
  };

  // Clear error
  const clearError = () => {
    dispatch({ type: AUTH_ACTIONS.CLEAR_ERROR });
  };

  // Context value
  const contextValue = {
    ...state,
    login,
    logout,
    updateUser,
    clearError,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;